//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';

import { CuralateCatalogProduct } from '@apis/universal-image-actions/universalImageActionsApiModel';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import { Product } from '@models/IItemMetadata';
import { useProductTaggingContext } from '@uia/products/context/ProductTaggingContext';
import { ProductEditorDetails } from '@uia/products/editor/curalate-product-editor/components/ProductEditorDetails';
import { ProductLoadingError } from '@uia/products/editor/curalate-product-editor/components/ProductLoadingError';
import { useCurrentTrackingParamsQuery } from '@uia/products/hooks/useCurrentTrackingParamsQuery';
import { isDefined } from '@utils/filterPredicates';

interface CuralateProductEditorProps {
  onClose: () => void;
  product: Product | CuralateCatalogProduct;
}

export const CuralateProductEditor = ({
  onClose,
  product,
}: CuralateProductEditorProps) => {
  const { metadata } = useProductTaggingContext();

  const {
    data: currentTrackingParams,
    isError,
    isPending,
  } = useCurrentTrackingParamsQuery(!metadata.disableProductTagging);

  const productTrackingParamsVersion =
    isDefined(product) && 'trackingParamsVersion' in product
      ? product.trackingParamsVersion
      : undefined;
  const trackingParamsVersion =
    productTrackingParamsVersion ?? currentTrackingParams;

  if (isPending) {
    return <CenteredSpinner />;
  }

  if (isError || !trackingParamsVersion) {
    return <ProductLoadingError />;
  }

  return (
    <ProductEditorDetails
      onClose={onClose}
      product={product}
      trackingParamsVersion={trackingParamsVersion}
    />
  );
};
